import React, {useCallback, useEffect, useState} from "react";
import PropTypes from "prop-types";
import {Badge, Card, Col, Row} from "react-bootstrap";
import Modals from "../../../components/modals";
import axios from "../../../utils/apiClient";
import {useLocation} from "react-router-dom";
import SubmitRecentVisitBody from "./../SubmitRecentVisit";
import ScheduledAppointment from "./modals/scheduleAppointment";
import ChangeStatusModal from "./modals/changeStatus";
import NoShowModal from "./modals/noShow";
import ResetStatusModal from "./modals/resetStatus";
import {ROUTES} from "../../../utils/navigationItems";
import PatientRow from "./PatientRow";
import {useDispatch, useSelector} from "react-redux";
import {setReloadPatientList} from "../../../actions/manageVisitActions";
import {format} from "date-fns";
import {getErrorCode, TRACK_EVENTS, trackPendoEvent} from "../../../utils/pendoTrack";

const componentName = "CardView";

export const referralTypes = [
  { text: "Care Coordination (Equality Health)", value: "CARE_COORDINATION" },
];

const buildVisitGapsUrl = (patientId, action, careGapId) => {
  const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
  const params = [
    `patientId=${encodeURIComponent(patientId ?? "")}`,
    `action=${encodeURIComponent(action ?? "")}`,
  ];

  if (careGapId !== undefined) {
    params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
  }

  return `${baseUrl}/v1/patients/getVisitGaps?${params.join("&")}`;
};

const buildUpdateVisitGapsUrl = (patientId, action, careGapId) => {
  const baseUrl = process.env.REACT_APP_REST_API_BASE_URL;
  const params = [
    `patientId=${encodeURIComponent(patientId ?? "")}`,
    `action=${encodeURIComponent(action ?? "")}`,
  ];

  if (careGapId !== undefined) {
    params.push(`careGapId=${encodeURIComponent(careGapId ?? "null")}`);
  }

  return `${baseUrl}/v1/patients/updateVisitGaps?${params.join("&")}`;
};

const toDateKey = (value) => {
  if (!value) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length >= 10) {
      const possible = trimmed.slice(0, 10);
      if (/^\d{4}-\d{2}-\d{2}$/.test(possible)) {
        return possible;
      }
    }
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return format(date, "yyyy-MM-dd");
};

const resolveVisitAppointmentDate = (visit) => {
  if (!visit) return null;
  return (
    visit.appointmentDate ||
    visit.patientGapAppointmentDate ||
    visit.visitDate ||
    visit.appointmentDateTime ||
    null
  );
};

const resolvePatientAppointmentDate = (patient) =>
  patient?.patientGapAppointmentDate ||
  patient?.appointmentDate ||
  patient?.appointmentDateTime ||
  null;

const resolveNoShowVisitsToUpdate = ({
  noShowVisits,
  noShowSelectedCareGapIds,
  primaryCareGapId,
  fallbackAppointmentDate,
}) => {
  const visitsWithIds = noShowVisits.filter((visit) => !!visit?.careGapId);
  const hasMultipleVisits = noShowVisits.length > 1;
  let visitsToUpdate = [];

  if (visitsWithIds.length) {
    const visitsById = new Map(
      visitsWithIds.map((visit) => [visit.careGapId, visit])
    );

    if (hasMultipleVisits) {
      const selected = noShowSelectedCareGapIds.filter((id) => visitsById.has(id));
      if (!selected.length) {
        return { error: "Select at least one visit to update." };
      }
      visitsToUpdate = selected.map((id) => visitsById.get(id)).filter(Boolean);
    } else {
      const fallbackVisit =
        visitsById.get(noShowSelectedCareGapIds[0]) ||
        visitsById.get(primaryCareGapId) ||
        visitsWithIds[0] ||
        null;
      if (fallbackVisit) {
        visitsToUpdate = [fallbackVisit];
      }
    }
  }

  if (visitsToUpdate.length) {
    return { visitsToUpdate };
  }

  if (!primaryCareGapId) {
    return { error: "No visits available to update." };
  }

  return {
    visitsToUpdate: [
      {
        careGapId: primaryCareGapId,
        appointmentDate: fallbackAppointmentDate,
      },
    ],
  };
};

const buildNoShowPayload = (visitsToUpdate, fallbackAppointmentDate) =>
  visitsToUpdate.map((visit) => ({
    careGapId: visit.careGapId,
    statusEnum: null,
    appointmentDate: resolveVisitAppointmentDate(visit) || fallbackAppointmentDate,
  }));

const resolveNoShowAppointmentDateKey = (patient) =>
  toDateKey(
    patient?.patientGapAppointmentDate ||
      patient?.appointmentDate ||
      patient?.appointmentDateTime ||
      patient?.nextVisit ||
      null
  );

const buildNoShowFallbackVisits = (patient, primaryCareGapId) =>
  primaryCareGapId
    ? [
        {
          careGapId: primaryCareGapId,
          description: patient?.displayName || patient?.gapDescription || "Scheduled visit",
          appointmentDate: resolvePatientAppointmentDate(patient),
        },
      ]
    : [];

const resolveNoShowCandidateVisits = (responseData, appointmentDateKey) => {
  const rawSameDay = Array.isArray(responseData?.sameDayVisits)
    ? responseData.sameDayVisits
    : [];
  const otherVisits = [
    ...(Array.isArray(responseData?.dueNowVisits) ? responseData.dueNowVisits : []),
    ...(Array.isArray(responseData?.dueSoonVisits) ? responseData.dueSoonVisits : []),
    ...(Array.isArray(responseData?.scheduledVisits) ? responseData.scheduledVisits : []),
  ];

  if (!appointmentDateKey) {
    return rawSameDay.length ? rawSameDay : otherVisits;
  }

  const candidateVisits = rawSameDay.length ? rawSameDay : otherVisits;
  if (!candidateVisits.length) {
    return candidateVisits;
  }

  const sameDayMatches = candidateVisits.filter((visit) => {
    const visitDateKey = toDateKey(resolveVisitAppointmentDate(visit));
    return visitDateKey ? visitDateKey === appointmentDateKey : false;
  });

  return sameDayMatches.length ? sameDayMatches : candidateVisits;
};

const dedupeNoShowVisitsByCareGapId = (visits) => {
  const uniqueVisits = [];
  const trackedIds = new Set();

  for (const visit of visits) {
    if (!visit) continue;
    const careGapId =
      visit.careGapId ??
      visit.patientGapId ??
      visit.patientGapID ??
      visit.careGapID ??
      null;

    if (!careGapId || trackedIds.has(careGapId)) continue;
    trackedIds.add(careGapId);
    uniqueVisits.push({
      ...visit,
      careGapId,
    });
  }

  return uniqueVisits;
};

const resolveNoShowSelectedCareGapIds = (visits, normalizedPrimaryCareGapId) => {
  const normalizedVisits = Array.isArray(visits) ? visits : [];
  const preferredVisit =
    normalizedVisits.find((visit) => {
      if (!visit?.careGapId || !normalizedPrimaryCareGapId) return false;
      return String(visit.careGapId).trim() === normalizedPrimaryCareGapId;
    }) ||
    normalizedVisits.find((visit) => !!visit?.careGapId) ||
    null;

  return preferredVisit?.careGapId ? [preferredVisit.careGapId] : [];
};

const ListView = ({
    patientList,
    handleChangeCheckBox,
    handleAllPatientsCheckbox,
    workListStatusSize,
    loadingData,
    listViewLoadingData,
    quickListType,
    isPatientSelected,
    updateParent,
    closeEvidenceModal,
    handleFilterChange,
    filterParams,
    pageNumber,
    filterApplied,
    selectedFilter,
    activeFilterView
  }) => {
  const isEmpty = require("is-empty");
  const dispatch = useDispatch();
  const [selectedPatient, setSelectedPatient] = useState({});
  const [confirmationMessage, setConfirmationMessage] = useState(false);
  const [showRecentVisitModal, setShowRecentVisitModal] = useState(false);
  const [showPatientDetails, setShowPatientDetails] = useState(false);

  // New central action state
  const [activeAction, setActiveAction] = useState(null);
  const [visitGaps, setVisitGaps] = useState({ dueNowVisits: [], dueSoonVisits: [] });
  const [visitGapSelections, setVisitGapSelections] = useState({});
  const [visitGapsLoading, setVisitGapsLoading] = useState(false);
  const [visitGapsError, setVisitGapsError] = useState(null);
  const [visitGapsSubmitting, setVisitGapsSubmitting] = useState(false);
  const [changeStatusFormError, setChangeStatusFormError] = useState(null);
  const [noShowVisits, setNoShowVisits] = useState([]);
  const [noShowPrimaryCareGapId, setNoShowPrimaryCareGapId] = useState(null);
  const [noShowSelectedCareGapIds, setNoShowSelectedCareGapIds] = useState([]);
  const [noShowLoading, setNoShowLoading] = useState(false);
  const [noShowSubmitting, setNoShowSubmitting] = useState(false);
  const [noShowError, setNoShowError] = useState(null);
  const [noShowSubmitError, setNoShowSubmitError] = useState(null);
  const [resetStatusSubmitting, setResetStatusSubmitting] = useState(false);
  const [resetStatusSubmitError, setResetStatusSubmitError] = useState(null);

  const resetChangeStatusState = useCallback(() => {
    setVisitGaps({ dueNowVisits: [], dueSoonVisits: [] });
    setVisitGapSelections({});
    setVisitGapsError(null);
    setChangeStatusFormError(null);
    setVisitGapsLoading(false);
    setVisitGapsSubmitting(false);
  }, []);

  const resetNoShowState = useCallback(() => {
    setNoShowVisits([]);
    setNoShowPrimaryCareGapId(null);
    setNoShowSelectedCareGapIds([]);
    setNoShowError(null);
    setNoShowSubmitError(null);
    setNoShowLoading(false);
    setNoShowSubmitting(false);
  }, []);

  const resetResetStatusState = useCallback(() => {
    setResetStatusSubmitting(false);
    setResetStatusSubmitError(null);
  }, []);

  const resolveCareGapId = useCallback((patient) => {
    if (!patient) return null;

    const addCandidate = (collection, value) => {
      if (value === undefined || value === null) return;
      const normalized = typeof value === "string" ? value.trim() : value;
      if (normalized !== "" && normalized !== null) {
        collection.push(normalized);
      }
    };

    const candidates = [];

    addCandidate(candidates, patient.nextVisitId);
    addCandidate(candidates, patient.nextVisitID);
    addCandidate(candidates, patient.nextVisitGapId);
    addCandidate(candidates, patient.nextVisitGapID);

    addCandidate(candidates, patient.careGapId);
    addCandidate(candidates, patient.careGapID);
    addCandidate(candidates, patient.patientGapId);
    addCandidate(candidates, patient.patientGapID);

    if (Array.isArray(patient.careGapIds) && patient.careGapIds.length) {
      addCandidate(candidates, patient.careGapIds[0]);
    } else if (typeof patient.careGapIds === "string" && patient.careGapIds.length) {
      addCandidate(candidates, patient.careGapIds.split(",")[0]);
    }

    if (Array.isArray(patient.patientGapIds) && patient.patientGapIds.length) {
      addCandidate(candidates, patient.patientGapIds[0]);
    } else if (typeof patient.patientGapIds === "string" && patient.patientGapIds.length) {
      addCandidate(candidates, patient.patientGapIds.split(",")[0]);
    }

    return candidates.find((id) => id !== undefined && id !== null && id !== "") || null;
  }, []);

  const loadVisitGaps = useCallback(async (patient) => {
    if (!patient?.id) {
      return;
    }

    setVisitGapsLoading(true);
    setVisitGapsError(null);

    try {
      const careGapId = resolveCareGapId(patient);
      const normalizedPrimaryCareGapId =
        careGapId === undefined || careGapId === null
          ? null
          : String(careGapId).trim();
      const url = buildVisitGapsUrl(patient.id, "CHANGE_STATUS", careGapId);

      const response = await axios.get(url, {
        headers: { "Content-Type": "application/json" },
      });

      const dueNowVisits = response?.data?.dueNowVisits || [];
      const dueSoonVisits = response?.data?.dueSoonVisits || [];

      const initialSelections = {};
      const primeSelections = (visits) => {
        if (!visits) return;
        for (const visit of visits) {
          if (!visit?.careGapId) continue;
          const visitCareGapId = String(visit.careGapId).trim();
          const shouldSelectByDefault =
            !!visit.selected ||
            (!!normalizedPrimaryCareGapId &&
              normalizedPrimaryCareGapId.length &&
              normalizedPrimaryCareGapId === visitCareGapId);

          initialSelections[visit.careGapId] = {
            careGapId: visit.careGapId,
            selected: shouldSelectByDefault,
            statusEnum: visit.statusEnum && visit.statusEnum !== "OPEN" ? visit.statusEnum : "",
          };
        }
      };

      primeSelections(dueNowVisits);
      primeSelections(dueSoonVisits);

      setVisitGaps({ dueNowVisits, dueSoonVisits });
      setVisitGapSelections(initialSelections);
    } catch (error) {
      console.error("Failed to load visit gaps:", error);
      const message =
        error?.response?.data?.message || error?.message || "Unable to load visit gaps.";
      setVisitGapsError(message);
      setVisitGaps({ dueNowVisits: [], dueSoonVisits: [] });
      setVisitGapSelections({});
    } finally {
      setVisitGapsLoading(false);
    }
  }, [resolveCareGapId]);

  const loadNoShowVisits = useCallback(
    async (patient) => {
      if (!patient?.id) {
        return;
      }

      const primaryCareGapId = resolveCareGapId(patient);
      const normalizedPrimaryCareGapId =
        primaryCareGapId === undefined || primaryCareGapId === null
          ? null
          : String(primaryCareGapId).trim();
      setNoShowPrimaryCareGapId(primaryCareGapId);
      setNoShowError(null);
      setNoShowSubmitError(null);
      setNoShowLoading(true);

      const appointmentDateKey = resolveNoShowAppointmentDateKey(patient);
      const fallbackVisits = buildNoShowFallbackVisits(patient, primaryCareGapId);

      try {
        const url = buildVisitGapsUrl(patient.id, "NO_SHOW", primaryCareGapId);

        const response = await axios.get(url, {
          headers: { "Content-Type": "application/json" },
        });

        const candidateVisits = resolveNoShowCandidateVisits(
          response?.data,
          appointmentDateKey
        );
        const uniqueVisits = dedupeNoShowVisitsByCareGapId(candidateVisits);
        const visitsToUse = uniqueVisits.length ? uniqueVisits : fallbackVisits;

        setNoShowVisits(visitsToUse);
        setNoShowSelectedCareGapIds(
          resolveNoShowSelectedCareGapIds(visitsToUse, normalizedPrimaryCareGapId)
        );
      } catch (error) {
        console.error("Failed to load no-show visits:", error);
        const message =
          error?.response?.data?.message || error?.message || "Unable to load scheduled visits.";
        setNoShowError(message);
        setNoShowVisits(fallbackVisits);
        setNoShowSelectedCareGapIds(
          resolveNoShowSelectedCareGapIds(fallbackVisits, normalizedPrimaryCareGapId)
        );
      } finally {
        setNoShowLoading(false);
      }
    },
    [resolveCareGapId]
  );

  const updateNoShowSelection = useCallback(
    (careGapId, isSelected) => {
      if (!careGapId) return;
      setNoShowSubmitError(null);
      setNoShowSelectedCareGapIds((prev) => {
        if (isSelected) {
          if (prev.includes(careGapId)) {
            return prev;
          }
          return [...prev, careGapId];
        }
        return prev.filter((id) => id !== careGapId);
      });
    },
    [setNoShowSelectedCareGapIds, setNoShowSubmitError]
  );

  const updateVisitGapSelection = useCallback((careGapId, updates) => {
    if (!careGapId) return;
    setChangeStatusFormError(null);
    setVisitGapSelections((prev) => {
      const existing = prev[careGapId] || { careGapId, selected: false, statusEnum: "" };
      return {
        ...prev,
        [careGapId]: {
          ...existing,
          ...updates,
        },
      };
    });
  }, []);

  // One helper to open any modal
  const openAction = (action, patient) => {
    setSelectedPatient(patient);
    setActiveAction(action);
    resetResetStatusState();

    if (action === "changestatus") {
      resetChangeStatusState();
      loadVisitGaps(patient);
    } else if (action === "noshow") {
      resetNoShowState();
      loadNoShowVisits(patient);
    }
  };

  // One helper to close any modal
  const closeAction = useCallback(() => {
    if (activeAction === "changestatus") {
      resetChangeStatusState();
    }
    if (activeAction === "noshow") {
      resetNoShowState();
    }
    if (activeAction === "resetstatus") {
      resetResetStatusState();
    }
    setActiveAction(null);
  }, [activeAction, resetChangeStatusState, resetNoShowState, resetResetStatusState]);

  const handleSubmitChangeStatus = useCallback(async () => {
    if (visitGapsLoading || visitGapsSubmitting) return;

    const selections = Object.values(visitGapSelections).filter((selection) => selection.selected);

    if (!selections.length) {
      setChangeStatusFormError("Select at least one visit to update.");
      return;
    }

    const missingStatus = selections.find((selection) => !selection.statusEnum);
    if (missingStatus) {
      setChangeStatusFormError("Choose a status for each selected visit.");
      return;
    }

    if (!selectedPatient?.id) {
      setChangeStatusFormError("Patient information is missing.");
      return;
    }

    const payload = selections.map(({ careGapId, statusEnum, appointmentDate }) => ({
      careGapId,
      statusEnum,
      appointmentDate: appointmentDate || null,
    }));

    try {
      setVisitGapsSubmitting(true);
      setChangeStatusFormError(null);

      const url = buildUpdateVisitGapsUrl(
        selectedPatient.id,
        "CHANGE_STATUS",
        selections[0]?.careGapId ?? null
      );

      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_CHANGE_STATUS_SUBMIT, {
        route: window?.location?.pathname,
        quicklist_type: quickListType,
        status_enum: selections.map((selection) => selection.statusEnum).join(","),
        source_module: "patientList.handleSubmitChangeStatus",
        success: true
      });

      if (typeof handleFilterChange === "function") {
        await handleFilterChange(filterParams, pageNumber);
      }

      closeAction();
    } catch (error) {
        console.error("Failed to update visits:", error);
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Unable to update visits. Please try again.";
        trackPendoEvent(TRACK_EVENTS.QUICKLIST_CHANGE_STATUS_SUBMIT, {
          route: window?.location?.pathname,
          quicklist_type: quickListType,
          source_module: "patientList.handleSubmitChangeStatus",
          success: false,
          error_code: getErrorCode(error, "quicklist_change_status_failed")
        });
        setChangeStatusFormError(message);
      } finally {
        setVisitGapsSubmitting(false);
      }
  }, [visitGapsLoading, visitGapsSubmitting, visitGapSelections, selectedPatient.id, quickListType, handleFilterChange, closeAction, filterParams, pageNumber]);

  const handleSubmitNoShow = useCallback(async () => {
    if (noShowLoading || noShowSubmitting) return;

    const patientId = selectedPatient?.id;
    if (!patientId) {
      setNoShowSubmitError("Patient information is missing.");
      return;
    }

    const primaryCareGapId = noShowPrimaryCareGapId || resolveCareGapId(selectedPatient);
    const fallbackAppointmentDate = resolvePatientAppointmentDate(selectedPatient);
    const { visitsToUpdate, error } = resolveNoShowVisitsToUpdate({
      noShowVisits,
      noShowSelectedCareGapIds,
      primaryCareGapId,
      fallbackAppointmentDate,
    });

    if (error) {
      setNoShowSubmitError(error);
      return;
    }

    const payload = buildNoShowPayload(visitsToUpdate, fallbackAppointmentDate);

    try {
      setNoShowSubmitting(true);
      setNoShowSubmitError(null);

      const url = buildUpdateVisitGapsUrl(
        patientId,
        "NO_SHOW",
        primaryCareGapId ?? payload[0]?.careGapId ?? null
      );

      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_NO_SHOW_SUBMIT, {
        route: window?.location?.pathname,
        quicklist_type: quickListType,
        source_module: "patientList.handleSubmitNoShow",
        success: true
      });

      if (typeof handleFilterChange === "function") {
        await handleFilterChange(filterParams, pageNumber);
      }

      closeAction();
    } catch (error) {
      console.error("Failed to update no-show visits:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to update visits. Please try again.";
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_NO_SHOW_SUBMIT, {
        route: window?.location?.pathname,
        quicklist_type: quickListType,
        source_module: "patientList.handleSubmitNoShow",
        success: false,
        error_code: getErrorCode(error, "quicklist_no_show_failed")
      });
      setNoShowSubmitError(message);
    } finally {
      setNoShowSubmitting(false);
    }
  }, [noShowLoading, noShowSubmitting, selectedPatient, noShowPrimaryCareGapId, resolveCareGapId, noShowVisits, noShowSelectedCareGapIds, quickListType, handleFilterChange, closeAction, filterParams, pageNumber]);

  const handleSubmitResetStatus = useCallback(async () => {
    if (resetStatusSubmitting) return;

    const patientId = selectedPatient?.id;
    if (!patientId) {
      setResetStatusSubmitError("Patient information is missing.");
      return;
    }

    const careGapId = resolveCareGapId(selectedPatient);
    if (!careGapId) {
      setResetStatusSubmitError("Unable to determine the visit to update.");
      return;
    }

    const payload = [
      {
        careGapId,
        statusEnum: "OPEN",
        appointmentDate: null,
      },
    ];

    try {
      setResetStatusSubmitting(true);
      setResetStatusSubmitError(null);

      const url = buildUpdateVisitGapsUrl(patientId, "CHANGE_STATUS", careGapId);

      await axios.post(url, payload, {
        headers: { "Content-Type": "application/json" },
      });
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_RESET_STATUS_SUBMIT, {
        route: window?.location?.pathname,
        quicklist_type: quickListType,
        source_module: "patientList.handleSubmitResetStatus",
        success: true
      });

      if (typeof handleFilterChange === "function") {
        await handleFilterChange(filterParams, pageNumber);
      }

      closeAction();
    } catch (error) {
      console.error("Failed to reset status:", error);
      const message =
        error?.response?.data?.message ||
        error?.message ||
        "Unable to reset status. Please try again.";
      trackPendoEvent(TRACK_EVENTS.QUICKLIST_RESET_STATUS_SUBMIT, {
        route: window?.location?.pathname,
        quicklist_type: quickListType,
        source_module: "patientList.handleSubmitResetStatus",
        success: false,
        error_code: getErrorCode(error, "quicklist_reset_status_failed")
      });
      setResetStatusSubmitError(message);
    } finally {
      setResetStatusSubmitting(false);
    }
  }, [resetStatusSubmitting, selectedPatient, resolveCareGapId, quickListType, handleFilterChange, closeAction, filterParams, pageNumber]);

  const visitPatient = useSelector((state) => state?.manageVisitReducer?.patient);
  const reloadPatients = useSelector((state) => state?.manageVisitReducer?.reload);

  useEffect(() => {
    if (reloadPatients) {
      handleFilterChange(filterParams, pageNumber);
      dispatch(setReloadPatientList(false));
    }
  }, [dispatch, filterParams, handleFilterChange, pageNumber, reloadPatients]);

  const closeEvidenceModalMain = () => {
    setConfirmationMessage(false);
    closeEvidenceModal();
  };

  const quickListState = useLocation();
  let pageTitle = quickListState.state ? quickListState.state.pageTitle : "";
  if (!pageTitle) {
    const pathName = quickListState.pathname ? quickListState.pathname : "";
    for (const [key, value] of Object.entries(ROUTES)) {
      if (value && pathName && value.path && value.path.split("/").join("") === pathName.split("/").join("")) {
        pageTitle = key;
      }
    }
  }

  useEffect(() => {
    if (visitPatient?.id) {
      setSelectedPatient(visitPatient);
      setShowPatientDetails(true);
    }
  }, [visitPatient]);

  const quickListMOType = (patient) => {
    if (patientList?.awaitingChart?.includes(patient)) {
      return 'awaitingChart';
    }
    else if (patientList?.rejected?.includes(patient)) {
      return 'rejected';
    }
    return 'other';
  };

  const titles =
    quickListType && quickListType === "MISSED_OPPORTUNITIES" ? {
      awaitingChart: ["Awaiting Chart", workListStatusSize[0]],
      rejected: ["Rejected", workListStatusSize[1]],
    } : {
      scheduledPatients: ["Scheduled", workListStatusSize[0]],
      unScheduledPatients: ["Unscheduled", workListStatusSize[1]],
      unableToContact: ["Unable to Contact", workListStatusSize[2]],
      patientRefused: ["Patient Refused", workListStatusSize[3]],
      leftMessage: ["Left Message", workListStatusSize[4]],
      changedPCP: ["Changed PCP", workListStatusSize[5]],
    };
  const isAllPatientsSelected = (patientGroup) => {
    return patientGroup.every((patient) => isPatientSelected(patient));
  };

  const handleSelectAllPatients = (patientGroup) => {
    const isAllSelected = isAllPatientsSelected(patientGroup);
    handleAllPatientsCheckbox(patientGroup, !isAllSelected);
  };
  const convertNumber = (number) => {
    if (number !== null && !Number.isNaN(Number(number))) {
      return Number(number).toLocaleString();
    } else {
      return "";
    }
  };
  return (
    <Row className="container-fluid p-0 m-0">
      <Col className="p-0">
        {Object.keys(patientList).map((key, index) => {
          return (
            <React.Fragment key={`${componentName}-group-${key}`}>
              {isEmpty(patientList[key]) ? (
                (() => {
                  const shouldShowNoPatients =
                    (quickListType === "USER_WORK_LIST" ||
                      quickListType === "SINGLE_WORK_LIST" ||
                      quickListType === "MISSED_OPPORTUNITIES") &&
                    key !== "default" &&
                    (filterApplied === false || selectedFilter.includes(key)) &&
                    key !== "scheduledPatients" &&
                    loadingData === false &&
                    listViewLoadingData === false;

                  if (!shouldShowNoPatients) return null;

                  return (
                    <Row id={`${componentName}-patientsSection-${index}`} className="mb-5">
                      <Col>
                        <div className="d-flex flex-row justify-content-start align-items-center gap-2 mb-2">
                          <h4 id={`${componentName}-patientsSectionTitle-${index}`} className="mb-0">
                            {titles[key][0]}
                          </h4>
                          <Badge bg="light" pill id={`${componentName}-patientCount-${index}`} className="border text-dark">
                            {titles[key][1]} {"Patients"}
                          </Badge>
                        </div>
                        <Card>
                          <Card.Body>
                            <Card.Text id={`${componentName}-NoPatients`}>No Patients Found</Card.Text>
                          </Card.Body>
                        </Card>
                      </Col>
                    </Row>
                  );
                })()
              ) : (
                <Row id={`${componentName}-patientsSection-${index}`} key={`${componentName}-patientsSection-${index}`} className="mb-5">
                  <Col key={`${componentName}-patientsSection-${index}`}>
                    {titles[key] && patientList[key] && patientList[key].length > 0 && (key !== "scheduledPatients") && (
                      <div className="d-flex flex-row justify-content-start align-items-center gap-2 mb-2">
                        <div className="form-check">
                          <input id={`${componentName}-patientSelectAll-${index}`} className="form-check-input" type="checkbox" name={`${componentName}-patientSelectAll-${index}`}
                            checked={isAllPatientsSelected(patientList[key])} onChange={() => handleSelectAllPatients(patientList[key])} />
                          <label htmlFor={`${componentName}-patientSelectAll-${index}`} className="visually-hidden">Select all patients in {titles[key][0]}</label>
                        </div>
                        <h4 id={`${componentName}-patientsSectionTitle-${index}`} className="mb-0">
                          {titles[key][0]}
                        </h4>
                        <Badge pill bg="light" id={`${componentName}-PatientCount-${index}`} className="border text-dark">
                          {convertNumber(titles[key][1])} Patients
                        </Badge>
                      </div>
                    )}

                    {patientList[key].map((patient, idx) => {
                      const referralId =
                        patient?.referral?.id ||
                        (Array.isArray(patient?.referralDtoList) && patient.referralDtoList[0]?.id);
                      const rowKey = `${key}-${patient?.id || patient?.patientId || patient?.memberId || "unknown"}-${referralId || idx}`;
                      return (
                      <PatientRow
                        key={rowKey}
                        quickListType={quickListType}
                        patient={patient}
                        idx={idx}
                        isPatientSelected={isPatientSelected}
                        handleChangeCheckBox={handleChangeCheckBox}
                        setSelectedPatient={setSelectedPatient}
                        setShowPatientDetails={setShowPatientDetails}
                        showPatientDetails={showPatientDetails}
                        pageTitle={pageTitle}
                        onAction={openAction}
                        listType={quickListMOType(patient)}
                        activeFilterView={activeFilterView}
                      />
                      );
                    })}
                  </Col>
                </Row>
              )}
            </React.Fragment>
          );
        })}
        {["schedule", "reschedule", "unschedule"].includes(activeAction) && (
          <ScheduledAppointment
            show={true}
            onHide={closeAction}
            patient={selectedPatient}
            appointmentAction={activeAction}
            onSuccess={async () => {
              if (typeof handleFilterChange === "function") {
                await handleFilterChange(filterParams, pageNumber);
              }
            }}
          />
        )}

        {activeAction === "noshow" && (
          <NoShowModal
            show={true}
            onHide={closeAction}
            onSubmit={handleSubmitNoShow}
            patient={selectedPatient}
            visits={noShowVisits}
            selectedCareGapIds={noShowSelectedCareGapIds}
            updateSelection={updateNoShowSelection}
            loading={noShowLoading}
            submitting={noShowSubmitting}
            error={noShowError}
            submitError={noShowSubmitError}
          />
        )}

        {activeAction === "changestatus" && (
          <ChangeStatusModal
            show={true}
            onHide={closeAction}
            onSubmit={handleSubmitChangeStatus}
            patient={selectedPatient}
            visitGaps={visitGaps}
            visitGapSelections={visitGapSelections}
            visitGapsLoading={visitGapsLoading}
            visitGapsError={visitGapsError}
            changeStatusFormError={changeStatusFormError}
            visitGapsSubmitting={visitGapsSubmitting}
            updateVisitGapSelection={updateVisitGapSelection}
          />
        )}

        {activeAction === "resetstatus" && (
          <ResetStatusModal
            show={true}
            onHide={closeAction}
            onSubmit={handleSubmitResetStatus}
            patient={selectedPatient}
            submitting={resetStatusSubmitting}
            submitError={resetStatusSubmitError}
          />
        )}
        <Modals
          centered={true}
          show={confirmationMessage}
          scrollable={true}
          closeConfirmation={() => setConfirmationMessage(false)}
          onCreate={() => closeEvidenceModalMain()}
          onHide={() => setConfirmationMessage(false)}
          size="sm"
          title=""
          titleid={`${componentName}-CloseWithoutSavingModal`}
          closeBtn="No"
          closeBtnId={`${componentName}-closeNo`}
          completeBtn="Yes"
          completeBtnId={`${componentName}-completeYes`}
          hideFooter={false}
          body="Do you want to close without saving?"
        />
        <Modals
          scrollable={true}
          show={showRecentVisitModal}
          closeSubmitModal={() => setShowRecentVisitModal(false)}
          onHide={() => {
            setShowRecentVisitModal(false);
            updateParent();
          }}
          size="xl"
          title="Update Last Wellness Visit"
          titleid={`${componentName}-SubmitRecentVisitModal`}
          completeBtn="Submit"
          completeBtnId={`${componentName}-completeRecentVisitSubmit`}
          hideFooter={true}
          body={
            <SubmitRecentVisitBody
              patient={selectedPatient}
              updateParent={updateParent}
              closeSubmitModal={() => setShowRecentVisitModal(false)}
            />
          }
        />
      </Col>
    </Row>
  );
};

ListView.propTypes = {
  patientList: PropTypes.object.isRequired,
  handleChangeCheckBox: PropTypes.func,
  handleAllPatientsCheckbox: PropTypes.func,
  workListStatusSize: PropTypes.array,
  loadingData: PropTypes.bool,
  listViewLoadingData: PropTypes.bool,
  quickListType: PropTypes.string,
  isPatientSelected: PropTypes.func,
  updateParent: PropTypes.func,
  closeEvidenceModal: PropTypes.func,
  handleFilterChange: PropTypes.func,
  filterParams: PropTypes.oneOfType([
    PropTypes.object,
    PropTypes.arrayOf(
      PropTypes.shape({
        paramName: PropTypes.string,
        paramValue: PropTypes.any,
        displayValue: PropTypes.any,
      })
    ),
  ]),
  pageNumber: PropTypes.number,
  filterApplied: PropTypes.bool,
  selectedFilter: PropTypes.array,
  activeFilterView: PropTypes.string,
};

export default ListView;
